<template>
  <div
    class="select-field"
    :class="{ 'select-field--invalid is-invalid': dirty && !!errorMessage }"
    :data-invalid="dirty && !!errorMessage"
  >
    <label class="select-field__label" :for="`select-${Math.random()}`">
      {{ inputPlaceholder }}
      <div class="select-field__wrapper">
        <select
          v-model="inputValue"
          :class="['select-field__select', customClass]"
          :disabled="disabled"
          :aria-label="label"
          :data-small="isSmall"
        >
          <option
            class="select-field__option select-field__option--placeholder"
            disabled
            value=""
          >
            {{ inlineOption || inputPlaceholder }}
          </option>
          <option
            v-for="option in options"
            v-text="option.text || option"
            class="select-field__option"
            :key="option.value || option"
            :value="option.value || option"
          />
        </select>
      </div>
      <small v-if="!hideErrorMessage" class="select-field__error-message">
        {{ errorMessage }}
      </small>
    </label>
  </div>
</template>

<script setup>
import { computed } from 'vue';

const emit = defineEmits(['update:modelValue']);

const props = defineProps({
  modelValue: { type: [String, Number], default: '' },
  label: { type: String, default: '' },
  hideErrorMessage: { type: Boolean, required: false },
  inlineOption: { type: String, required: false },
  options: { type: Array, default: () => [] },
  fixedLabel: { type: Boolean, default: false },
  required: { type: Boolean, default: false },
  disabled: { type: Boolean, default: false },
  dirty: { type: Boolean, default: false },
  customClass: { type: String, default: '' },
  errorMessage: { type: String, default: '' },
  isSmall: { type: Boolean, default: false },
});

const inputPlaceholder = computed(() => (props.required ? `${props.label}*` : props.label));

const inputValue = computed({
  get: () => props.modelValue,
  set(value) {
    emit('update:modelValue', value);
  },
});
</script>

<style scoped lang="scss">
.select-field {
  &__label {
    font-size: 18px;

    @media (min-width: 1281px) {
      font-size: 20px;
    }
  }

  &__select {
    width: 100%;
    height: 50px;
    display: flex;
    align-items: center;
    padding: 0 12px;
    border: 2px solid $AC_Offwhite;
    border-radius: 5px;
    background-color: #fafafa;
    font-size: clamp(18px, 24px, 1.5vw);
    cursor: pointer;
    appearance: none;
    background-image: url('@/assets/img/chevron-down.svg');
    background-size: 32px;
    background-repeat: no-repeat;
    background-position-x: 98.5%;
    background-position-y: 50%;

    @media (min-width: 1281px) {
      height: 60px;
    }

    &:hover,
    &:focus {
      border-color: $AC_Orange;
    }

    &:focus {
      outline-offset: 3px;
    }
  }

  &.is-invalid {
    .select-field__select {
      border-color: $AC_Primary;
    }
  }

  &__error-message {
    font-size: clamp(14px, 18px, 1.25vw);
    color: $AC_Primary;
  }

  &__option {
    color: $AC_Black;
  }
}

// Acura specific styles
.acura {
  .select-field {
    &__label,
    &__select,
    &__option {
      font-family: 'Avenir Book';
    }

    &__error-message {
      font-family: 'Avenir';
    }
  }
}

// Honda specific styles
.honda {
  .select-field {
    &__label,
    &__select,
    &__option {
      font-family: 'Myriad Pro';
    }

    &__error-message {
      font-family: 'Myriad Pro';
    }
  }
}
</style>
